import React, {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo
} from 'react'

import {
  DefaultValues,
  FieldErrors,
  FieldValues,
  FormProvider,
  useForm,
  UseFormGetValues,
  UseFormReset,
  UseFormResetField,
  UseFormSetValue
} from 'react-hook-form'

import { DevTool } from '@hookform/devtools'

import { DirtyContext } from './DirtyProvider'
import { CustomContext } from './CustomProvider'
import { Translation } from './Translation'

export interface RefType<T extends FieldValues> {
  reset: UseFormReset<T>
  resetField: UseFormResetField<T>
  setValue: UseFormSetValue<T>
  getValues: UseFormGetValues<T>
}

export type Props<T extends FieldValues> = React.PropsWithChildren<{
  defaultValues?: DefaultValues<T>
  values?: T
  onSubmit: (form: T) => Promise<void> | void
  readOnly?: boolean
  disabled?: boolean
  serverErrors?: FieldErrors<T>
  subscribe?: (data: T) => void
  ref?: RefObject<RefType<T> | null>
  formRef?: RefObject<HTMLFormElement | null>
  onSubmittedSuccessfully?: () => void
  overrideClassName?: string
  generateDefaultValues?: () => DefaultValues<T>
  translatableFields?: string[]
  enableDevTools?: boolean
}>

export interface ServerError {
  type: string
  message: string
}

export function Root<T extends FieldValues>(props: Props<T>) {
  const {
    children,
    defaultValues,
    values,
    serverErrors,
    readOnly = false,
    disabled = false,
    ref,
    formRef,
    overrideClassName,
    translatableFields = [],
    enableDevTools,
    subscribe,
    onSubmit,
    onSubmittedSuccessfully,
    generateDefaultValues
  } = props

  const id = useId()
  const { setIsDirty } = useContext(DirtyContext)

  const {
    control,
    resetField,
    reset,
    watch,
    formState,
    setValue,
    getValues,
    ...methods
  } = useForm<T>({
    disabled,
    errors: serverErrors,
    defaultValues,
    values,
    shouldFocusError: true
  })

  const onSubmitProxy = useCallback(
    async (data: T) => {
      await Promise.resolve(onSubmit(data)).catch(error => console.error(error))
    },
    [onSubmit]
  )

  useEffect(() => {
    if (!formState.isSubmitSuccessful) return

    reset(generateDefaultValues ? generateDefaultValues() : defaultValues)
    onSubmittedSuccessfully?.()
  }, [
    formState.isSubmitSuccessful,
    reset,
    defaultValues,
    onSubmittedSuccessfully,
    generateDefaultValues
  ])

  useEffect(() => {
    if (!subscribe) return

    const { unsubscribe } = watch(values => {
      subscribe(values as T)
    }, defaultValues)

    return () => unsubscribe()
  }, [defaultValues, watch, subscribe])

  useImperativeHandle(ref, () => {
    return {
      getValues,
      reset,
      resetField,
      setValue
    }
  }, [reset, resetField, setValue, getValues])

  useEffect(() => {
    setIsDirty(id, formState.isDirty)

    return () => setIsDirty(id, false)
  }, [id, formState.isDirty, setIsDirty])

  const customContextValue = useMemo(
    () => ({
      id,
      readOnly,
      disabled
    }),
    [id, readOnly, disabled]
  )

  return (
    <StopPropagation>
      <FormProvider
        {...methods}
        control={control}
        resetField={resetField}
        reset={reset}
        watch={watch}
        setValue={setValue}
        getValues={getValues}
        formState={formState}
      >
        <CustomContext value={customContextValue}>
          <Translation translatableFields={translatableFields}>
            <form
              id={id}
              className={overrideClassName ?? 'flex flex-col gap-2.5'}
              ref={formRef}
              aria-readonly={readOnly}
              onSubmit={e => {
                if (e.target !== e.currentTarget) return

                e.preventDefault()
                e.stopPropagation()

                if (readOnly) return

                methods.handleSubmit(onSubmitProxy)(e)
              }}
            >
              {children}
            </form>
            {enableDevTools && <DevTool control={control} />}
          </Translation>
        </CustomContext>
      </FormProvider>
    </StopPropagation>
  )
}

/**
 * Prevents event bubbling for nested forms.
 *
 * When forms are nested (e.g., a form in a portal/popover inside another form),
 * React's synthetic event system still bubbles events through the component tree
 * even though they're in different DOM locations. This causes issues where:
 * - Submit buttons in nested forms don't trigger their own form's submit event
 * - Events from nested forms can trigger parent form handlers
 *
 * This wrapper stops propagation of key events (onClick, onSubmit, onKeyDown)
 * so that nested forms can work independently. It only activates when inside
 * another CustomContext (indicating a nested form scenario).
 */
function StopPropagation({ children }: React.PropsWithChildren) {
  const { id } = useContext(CustomContext)

  // If not inside another form context, no need to stop propagation
  if (!id) return children

  return (
    <div
      onClick={e => e.stopPropagation()}
      onSubmit={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      {children}
    </div>
  )
}
